import type {
  Answer,
  FieldDefinition,
  FieldId,
  ValidationIssue,
} from '@inside-us-aos/domain';
import { isFieldRequired, validateAnswerValue } from '@inside-us-aos/domain';

import { resolveVisibleFieldIds } from '../engine/visibility';

/**
 * Section progress derivation.
 *
 * Each `FieldDefinition` carries a `sectionId`. This module groups the
 * catalog by section and returns per-section progress: the count of
 * visible+required fields, the count answered (validation-clean), the
 * percentage, a status, and the unanswered field ids.
 *
 * Statuses (intentionally conservative):
 *   - `not_started`  — zero answered required fields, OR the section
 *                      has no visible required fields right now.
 *   - `in_progress`  — at least one but not all visible required
 *                      fields are answered.
 *   - `complete`     — every visible required field is answered AND
 *                      validation-clean.
 *
 * The plan calls out a fourth `blocked` status for cross-section
 * visibility-rule dependencies; per the slice brief we explicitly
 * defer that heuristic and fall back to `not_started` when ambiguous.
 *
 * Hidden fields contribute neither to the required count nor to the
 * answered count (they are unsatisfiable). Optional visible fields
 * are not counted either — sections complete when their required
 * surface is clean, regardless of optional answers.
 */

export type SectionStatus = 'not_started' | 'in_progress' | 'complete';

export interface SectionProgress {
  sectionId: string;
  /** Count of visible+required fields in the section. */
  required: number;
  /** Count of visible+required fields with a validation-clean answer. */
  answered: number;
  /**
   * Field ids in the section that are visible+required but
   * unanswered or have a validation issue. Stable in catalog order.
   */
  missingFieldIds: readonly FieldId[];
  /**
   * Integer percentage in [0, 100]. When `required` is 0 the section
   * is reported as 100 (vacuously complete) so a UI summary doesn't
   * stall on always-optional sections.
   */
  percentComplete: number;
  status: SectionStatus;
}

const isAnswerCleanFor = (
  field: FieldDefinition,
  answer: Answer | undefined,
  answers: readonly Answer[],
): boolean => validateAnswerValue(field, answer?.value, answers).length === 0;

/**
 * Returns a per-section progress map keyed by `sectionId`. Sections
 * are sourced from the catalog itself — passing an empty answer set
 * still returns one entry per section the catalog knows about, with
 * status `not_started` (or `complete` when the section has no visible
 * required fields).
 */
export const resolveSectionProgress = (
  catalog: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): ReadonlyMap<string, SectionProgress> => {
  const visibleFieldIds = resolveVisibleFieldIds(catalog, answers);
  const answerByFieldId = new Map(
    answers.map(answer => [answer.fieldId, answer]),
  );

  const fieldsBySection = new Map<string, FieldDefinition[]>();
  for (const field of catalog) {
    const list = fieldsBySection.get(field.sectionId) ?? [];
    list.push(field);
    fieldsBySection.set(field.sectionId, list);
  }

  // Repeatable groups: a field tagged with a `repeatableGroupId` is
  // logically a template that fans out per instance. The catalog
  // doesn't know about instances; the engine derives instance presence
  // from observed answers (distinct `groupInstanceId` values across
  // the group's field ids). A group with zero observed instances
  // contributes NO required fields to its section — otherwise a user
  // who answered "0 children" / "no prior marriages" / "haven't used
  // other names" would see the section's missing-count banner fire on
  // phantom rows that don't exist. Once the user adds at least one
  // instance (an answer flows in tagged with a `groupInstanceId`),
  // the group's child fields start counting toward required.
  const groupHasInstance = new Map<string, boolean>();
  for (const answer of answers) {
    if (answer.groupInstanceId === undefined) continue;
    const field = catalog.find(f => f.id === answer.fieldId);
    if (!field?.repeatableGroupId) continue;
    groupHasInstance.set(field.repeatableGroupId, true);
  }
  const fieldContributesToRequired = (field: FieldDefinition): boolean => {
    if (!visibleFieldIds.has(field.id)) return false;
    if (!isFieldRequired(field, answers)) return false;
    if (
      field.repeatableGroupId !== undefined &&
      groupHasInstance.get(field.repeatableGroupId) !== true
    ) {
      return false;
    }
    return true;
  };

  const result = new Map<string, SectionProgress>();
  for (const [sectionId, fields] of fieldsBySection) {
    const requiredFields = fields.filter(fieldContributesToRequired);
    const missingFieldIds: FieldId[] = [];
    let answered = 0;
    for (const field of requiredFields) {
      const answer = answerByFieldId.get(field.id);
      if (isAnswerCleanFor(field, answer, answers)) {
        answered += 1;
      } else {
        missingFieldIds.push(field.id);
      }
    }

    const required = requiredFields.length;
    const percentComplete =
      required === 0 ? 100 : Math.round((answered / required) * 100);
    const status: SectionStatus =
      required === 0
        ? 'complete'
        : answered === 0
          ? 'not_started'
          : answered === required
            ? 'complete'
            : 'in_progress';

    result.set(sectionId, {
      sectionId,
      required,
      answered,
      missingFieldIds,
      percentComplete,
      status,
    });
  }
  return result;
};

/**
 * Convenience: resolve a single section's progress. Returns `null` if
 * the section id is unknown to the catalog.
 */
export const resolveSectionStatus = (
  catalog: readonly FieldDefinition[],
  sectionId: string,
  answers: readonly Answer[] = [],
): SectionProgress | null =>
  resolveSectionProgress(catalog, answers).get(sectionId) ?? null;

/**
 * Re-exported for tests / consumers that want to surface per-field
 * issues alongside section status. Identical shape to
 * `ValidationIssue` from the domain package — re-exported here for
 * documentation purposes only.
 */
export type { ValidationIssue };
